import React, {useMemo} from 'react';
import parse from 'html-react-parser';
import Button from 'js/components/Common/Button/Button';
import Heading from 'js/components/Common/Heading/Heading';
import Thumbnail from 'js/components/Common/Thumbnail/Thumbnail';
import ProductLabelsList from 'js/components/Common/ProductLabelList/ProductLabelsList';

import fetchData from './fetchData';
import classes from './EntitySliderCard.module.pcss';
import useFetchCallback from 'js/hooks/useFetchCallback/useFetchCallback';
import VisibilityViewerIcon from 'js/components/Common/VisibilityViewerIcon/VisibilityViewerIcon';
import MixPanelWrapper, {EventDataType} from 'js/servises/MixPanelWrapper';

export type EntityType = {
	id: string,
	uri: string,
	title: string,
	description: string,
	link: string,
	type: 'articles' | 'manuals' | 'videos' | 'courses'
}

export interface Props {
	data: EntityType,
	mixPanelEventData: EventDataType
}


const EntitySliderCard = ({data, mixPanelEventData}: Props) => {
	const {title, id, type, link, uri, description} = data || null;
	const buttonType: {
		type: 'ButtonColored' | 'ButtonUncolored' | 'ArticleButton' | 'VideoButton' | 'CourseButton' | 'ManualButton',
		text: string,
		eventName: string
	} = useMemo(() => {
		switch (type) {
			case 'articles': return {
				type: 'ArticleButton',
				text: 'Read Now',
				eventName: 'Open Article page'
			};
			case 'manuals': return {
				type: 'ManualButton',
				text: 'Download',
				eventName: 'Download Manual'
			};
			case 'videos': return {
				type: 'VideoButton',
				text: 'Watch Now',
				eventName: 'Open Video page'
			};
			case 'courses': return {
				type: 'CourseButton',
				text: 'Start Course',
				eventName: 'Course Start'
			};
			default: return {
				type: 'ButtonColored',
				text: '',
				eventName: ''
			};
		}
	}, [type]);

	const fileDownloadHandler = useFetchCallback({
		updateData: resultData => resultData?.manualsSingle?.updateDownloads,
		query: () => `${fetchData(id)}`,
		onStart: () => {
			const element = document.createElement('a');
			element.setAttribute('href', link);
			const ext = link.split('.').pop();
			element.setAttribute('download', `${parse(title)}.${ext}`);

			element.style.display = 'block';
			element.click();
		},
		onSuccess: async () => {
			await MixPanelWrapper.TrackEvent(
				'Download Manual',
				{
					value: data?.title,
					...mixPanelEventData
				}
			);
		},
		onFailure: () => {
			console.error('Error while downloading');
		},
		onError: (e) => {
			console.error(e);
		}
	});

	return (
		<div className={classes.EntitySliderCard}>
			<div className={classes.Wrap}>
				<Thumbnail sizeType={'M'} type={type} alt={title} additionalClass={classes.Thumbnail}/>
				<VisibilityViewerIcon id={parseInt(id)} />
				<Heading text={`${title}`} type={'h4'} />
				<ProductLabelsList pageId={parseInt(id)}/>
				<div className={classes.Description}>{parse(description || '')}</div>
			</div>
			<div className={classes.ButtonWrap}>
				{type === 'manuals' ?
					<Button type={'button'}
							handler={fileDownloadHandler} buttonType={buttonType?.type} text={buttonType?.text} /> :
					<Button type={'a'}
							link={`/${type}/${uri}`}
							handler={async () => {
								await MixPanelWrapper.TrackEvent(
									buttonType?.eventName,
									{
										value: data?.title,
										...mixPanelEventData
									}
								);
							}}
							buttonType={buttonType?.type}
							text={buttonType?.text} />
				}
			</div>
		</div>
	);
};

export default EntitySliderCard;
